import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, MapPin, CalendarDays, Tag } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { MIAMI_IMAGE, LISBON_IMAGE } from "../assets/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Special slugs that keep their bespoke, hand-crafted public pages.
// Everything else uses the generic /eventos/:slug route powered by the
// admin-managed events collection.
const SPECIAL_ROUTES = {
  "apresentacao-setembro-2026": "/apresentacao-setembro-2026",
  "congresso-miami-2026": "/congresso-miami-2026",
};
const SPECIAL_IMAGES = {
  "apresentacao-setembro-2026": LISBON_IMAGE,
  "congresso-miami-2026": MIAMI_IMAGE,
};

const pick = (ev, base, lang) =>
  ev[`${base}_${lang}`] || ev[`${base}_pt`] || ev[`${base}_en`] || ev[`${base}_es`] || "";

export default function Events() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/events`);
        setItems(r.data?.items || []);
      } catch (e) {
        setItems([]);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  // Hardcoded fallback content used to enrich the two flagship events
  // with the same overline/body copy already translated in the i18n bundle.
  const enrich = (ev) => {
    const slug = ev.slug;
    if (slug === "apresentacao-setembro-2026") {
      return {
        overline: t.home.septCard.date,
        title: t.home.septCard.title,
        body: t.home.septCard.body,
        cta: t.home.septCard.cta,
        where: ev.location || "Lisboa · Portugal",
        when: ev.date_str || "Setembro 2026",
        image: ev.image_url || SPECIAL_IMAGES[slug],
      };
    }
    if (slug === "congresso-miami-2026") {
      return {
        overline: t.home.miamiCard.date,
        title: t.home.miamiCard.title,
        body: t.home.miamiCard.body,
        cta: t.home.miamiCard.cta,
        where: ev.location || "Miami · EUA",
        when: ev.date_str || "Setembro 2027",
        image: ev.image_url || SPECIAL_IMAGES[slug],
      };
    }
    return {
      overline: ev.date_str || "",
      title: pick(ev, "title", lang) || ev.slug,
      body: pick(ev, "subtitle", lang) || pick(ev, "body", lang).slice(0, 220),
      cta: t.common?.register || "Saber mais",
      where: ev.location || "",
      when: ev.date_str || "",
      image: ev.image_url,
    };
  };

  return (
    <div data-testid="events-page">
      <section className="container-page pt-20 sm:pt-28 pb-12">
        <div className="max-w-3xl">
          <div className="overline">{t.nav.events}</div>
          <h1 className="display-heading text-5xl sm:text-6xl mt-5 leading-[1.05]">{t.nav.events}.</h1>
          <span className="accent-rule mt-8 block" />
          <p className="mt-8 text-lg leading-relaxed text-[hsl(var(--muted-foreground))]">
            {t.home.eventsTitle}
          </p>
        </div>
      </section>

      <section className="container-page pb-24 space-y-12">
        {!loaded && (
          <p className="text-[hsl(var(--muted-foreground))]" data-testid="events-loading">A carregar…</p>
        )}
        {loaded && items.length === 0 && (
          <p className="text-[hsl(var(--muted-foreground))]" data-testid="events-empty">
            Sem eventos publicados. Volte em breve.
          </p>
        )}
        {items.map((ev) => {
          const enriched = enrich(ev);
          const to = SPECIAL_ROUTES[ev.slug] || `/eventos/${ev.slug}`;
          return (
            <Link
              key={ev.slug}
              to={to}
              className="group grid lg:grid-cols-12 gap-0 border border-[hsl(var(--border))] overflow-hidden hover:border-[hsl(var(--primary))] transition-all bg-white"
              data-testid={`events-card-${ev.slug}`}
            >
              <div className="lg:col-span-5 relative min-h-[280px] bg-[hsl(var(--primary))]">
                {enriched.image && (
                  <>
                    <img
                      src={enriched.image}
                      alt={enriched.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-[hsl(var(--primary))]/30" />
                  </>
                )}
              </div>
              <div className="lg:col-span-7 p-8 sm:p-12 flex flex-col justify-center">
                {enriched.overline && (
                  <div className="text-[hsl(var(--accent))] text-xs tracking-[0.3em] uppercase font-semibold">
                    {enriched.overline}
                  </div>
                )}
                <h2 className="mt-4 display-heading text-3xl sm:text-4xl group-hover:text-[hsl(var(--primary))] transition-colors">
                  {enriched.title}
                </h2>
                {enriched.body && (
                  <p className="mt-5 text-[hsl(var(--muted-foreground))] leading-relaxed">{enriched.body}</p>
                )}
                <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[hsl(var(--foreground))]">
                  {enriched.where && (
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[hsl(var(--accent))]" /> {enriched.where}
                    </span>
                  )}
                  {enriched.when && (
                    <span className="inline-flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-[hsl(var(--accent))]" /> {enriched.when}
                    </span>
                  )}
                  {ev.price_eur > 0 && (
                    <span className="inline-flex items-center gap-2">
                      <Tag className="h-4 w-4 text-[hsl(var(--accent))]" /> {Number(ev.price_eur).toFixed(0)} €
                    </span>
                  )}
                </div>
                <div className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[hsl(var(--primary))] font-medium">
                  {enriched.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          );
        })}
      </section>
    </div>
  );
}
